import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShop } from '../App';
import CustomJobModal from '../components/CustomJobModal';
import TransactionModal from '../components/TransactionModal';
import { useToast } from '../hooks/useToast';
import { ToastContainer } from '../components/ToastContainer';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const TICKET_STATUSES = [
  { value: 'new', label: 'New', color: 'bg-gray-100 text-gray-800' },
  { value: 'in-progress', label: 'In Progress', color: 'bg-blue-100 text-blue-800' },
  { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-800' },
  { value: 'on-hold', label: 'On Hold', color: 'bg-yellow-100 text-yellow-800' }
];

const JEWELRY_TYPES = ['Ring', 'Earrings', 'Necklace', 'Bracelet', 'Watch', 'Other'];

export default function CustomJobs() {
  const navigate = useNavigate();
  const { shops } = useShop();
  const [customJobs, setCustomJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCustomJob, setEditingCustomJob] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { toasts, success, error: toastError } = useToast();

  // Transaction modal state
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);

  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [filterStatus, setFilterStatus] = useState('any');
  const [filterJewelryType, setFilterJewelryType] = useState('any');
  const [filterDueType, setFilterDueType] = useState('any');
  const [filterShopId, setFilterShopId] = useState(() => localStorage.getItem('activeShopId') || '');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [quickRange, setQuickRange] = useState('');
  const [showingRecent, setShowingRecent] = useState(true);

  // Helper: format date as YYYY-MM-DD
  const formatDateInput = (d) => d.toISOString().split('T')[0];
  const today = new Date();

  // Quick date range handlers
  const setThisWeek = () => {
    const sundayThisWeek = new Date(today);
    sundayThisWeek.setDate(today.getDate() - today.getDay());
    setDateFrom(formatDateInput(sundayThisWeek));
    setDateTo(formatDateInput(today));
  };
  const setLastWeek = () => {
    const sundayThisWeek = new Date(today);
    sundayThisWeek.setDate(today.getDate() - today.getDay());
    const sundayLastWeek = new Date(sundayThisWeek);
    sundayLastWeek.setDate(sundayThisWeek.getDate() - 7);
    setDateFrom(formatDateInput(sundayLastWeek));
    setDateTo(formatDateInput(sundayThisWeek));
  };
  const setThisMonth = () => {
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    setDateFrom(formatDateInput(monthStart));
    setDateTo(formatDateInput(today));
  };
  const setLastMonth = () => {
    const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    setDateFrom(formatDateInput(lastMonthStart));
    setDateTo(formatDateInput(thisMonthStart));
  };
  const setThisQuarter = () => {
    const quarter = Math.floor(today.getMonth() / 3);
    const quarterStart = new Date(today.getFullYear(), quarter * 3, 1);
    setDateFrom(formatDateInput(quarterStart));
    setDateTo(formatDateInput(today));
  };
  const setLastQuarter = () => {
    const currentQuarter = Math.floor(today.getMonth() / 3);
    const thisQuarterStart = new Date(today.getFullYear(), currentQuarter * 3, 1);
    const lastQuarterStart = new Date(today.getFullYear(), (currentQuarter - 1) * 3, 1);
    setDateFrom(formatDateInput(lastQuarterStart));
    setDateTo(formatDateInput(thisQuarterStart));
  };
  const setThisYear = () => {
    const yearStart = new Date(today.getFullYear(), 0, 1);
    setDateFrom(formatDateInput(yearStart));
    setDateTo(formatDateInput(today));
  };
  const setLastYear = () => {
    const lastYear = today.getFullYear() - 1;
    const lastYearStart = new Date(lastYear, 0, 1);
    const thisYearStart = new Date(today.getFullYear(), 0, 1);
    setDateFrom(formatDateInput(lastYearStart));
    setDateTo(formatDateInput(thisYearStart));
  };
  const setAllTime = () => {
    setDateFrom('2000-01-01');
    setDateTo(formatDateInput(today));
  };

  const handleQuickRangeChange = (e) => {
    const range = e.target.value;
    setQuickRange(range);
    const rangeHandlers = {
      'this-week': setThisWeek, 'last-week': setLastWeek,
      'this-month': setThisMonth, 'last-month': setLastMonth,
      'this-quarter': setThisQuarter, 'last-quarter': setLastQuarter,
      'this-year': setThisYear, 'last-year': setLastYear,
      'all-time': setAllTime,
    };
    if (rangeHandlers[range]) {
      rangeHandlers[range]();
    } else {
      setDateFrom('');
      setDateTo('');
    }
  };

  const resetFilters = () => {
    setFilterStatus('any');
    setFilterJewelryType('any');
    setFilterDueType('any');
    setFilterShopId(localStorage.getItem('activeShopId') || '');
    setDateFrom('');
    setDateTo('');
    setQuickRange('');
    setShowFilters(false);
  };

  const toggleFilters = () => {
    if (showFilters) {
      resetFilters();
    } else {
      setShowFilters(true);
    }
  };

  const hasActiveFilters = () => {
    return filterStatus !== 'any' || filterJewelryType !== 'any' || filterDueType !== 'any' || dateFrom || dateTo;
  };

  // Initial fetch on mount (immediate, no debounce)
  useEffect(() => {
    setLoading(true);
    setShowingRecent(true);
    const params = new URLSearchParams();
    params.set('limit', '100');
    if (filterShopId) params.set('shop_id', filterShopId);
    fetch(`${API_URL}/custom-jobs?${params.toString()}`, { credentials: 'include' })
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch custom jobs');
        return res.json();
      })
      .then(data => setCustomJobs(data.custom_jobs || []))
      .catch(err => toastError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const initialShopRef = useRef(filterShopId);
  const mountedRef = useRef(false);

  // Debounced refetch when search or filters change (not on initial mount)
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    const debounceTimer = setTimeout(() => {
      if (searchQuery.length >= 3 || hasActiveFilters() || filterShopId !== initialShopRef.current) {
        setShowingRecent(false);
        fetchCustomJobs();
      } else if (searchQuery.length > 0 && searchQuery.length < 3) {
        setCustomJobs([]);
        setShowingRecent(false);
      } else if (!showingRecent) {
        setShowingRecent(true);
        const fetchRecent = async () => {
          setLoading(true);
          try {
            const params = new URLSearchParams();
            params.set('limit', '100');
            if (filterShopId) params.set('shop_id', filterShopId);
            const res = await fetch(`${API_URL}/custom-jobs?${params.toString()}`, { credentials: 'include' });
            if (!res.ok) throw new Error('Failed to fetch custom jobs');
            const data = await res.json();
            setCustomJobs(data.custom_jobs || []);
          } catch (err) {
            toastError(err.message);
          } finally {
            setLoading(false);
          }
        };
        fetchRecent();
      }
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, filterStatus, filterJewelryType, filterDueType, filterShopId, dateFrom, dateTo, showingRecent]);

  const fetchCustomJobs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', '100');
      if (searchQuery) params.set('search', searchQuery);
      if (filterStatus !== 'any') params.set('ticket_status', filterStatus);
      if (filterJewelryType !== 'any') params.set('jewelry_type', filterJewelryType);
      if (filterDueType !== 'any') params.set('due_type', filterDueType);
      if (filterShopId) params.set('shop_id', filterShopId);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);

      const queryString = params.toString();
      const url = queryString ? `${API_URL}/custom-jobs?${queryString}` : `${API_URL}/custom-jobs`;

      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch custom jobs');
      const data = await res.json();
      setCustomJobs(data.custom_jobs || []);
    } catch (err) {
      toastError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingCustomJob(null);
    setShowModal(true);
  };

  const openEditModal = (customJob) => {
    setEditingCustomJob(customJob);
    setShowModal(true);
  };

  const handleGoToTransaction = async (transactionId) => {
    try {
      const res = await fetch(`${API_URL}/transactions/${transactionId}`, {
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to fetch transaction');
      const data = await res.json();
      const transactionWithCustomer = {
        ...data,
        customer: {
          id: data.customer_id,
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          mobile_phone: data.mobile_phone || '',
          home_phone: data.home_phone || '',
          work_phone: data.work_phone || '',
          email: data.email || '',
          address_line1: data.address_line1 || '',
          address_line2: data.address_line2 || '',
          city: data.city || '',
          state: data.state || '',
          postal_code: data.postal_code || ''
        }
      };
      setEditingTransaction(transactionWithCustomer);
      setShowTransactionModal(true);
    } catch (err) {
      toastError(err.message);
    }
  };

  const handleTransactionModalSuccess = () => {
    setShowTransactionModal(false);
    setEditingTransaction(null);
    fetchCustomJobs();
  };

  const handleTransactionModalClose = () => {
    setShowTransactionModal(false);
    setEditingTransaction(null);
  };

  const handleModalSuccess = () => {
    success(editingCustomJob ? 'Custom job updated successfully' : 'Custom job created successfully');
    setShowModal(false);
    setEditingCustomJob(null);
    fetchCustomJobs();
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingCustomJob(null);
    fetchCustomJobs();
  };

  const handlePrintTicket = (customJobId) => {
    event?.stopPropagation?.();
    window.open(`${API_URL}/custom-jobs/${customJobId}/pdf?shop=ejr`, '_blank');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this custom job?')) return;
    try {
      const res = await fetch(`${API_URL}/custom-jobs/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to delete custom job');
      success('Custom job deleted successfully');
      fetchCustomJobs();
    } catch (err) {
      toastError(err.message);
    }
  };

  const getStatusBadge = (status) => {
    const statusInfo = TICKET_STATUSES.find(s => s.value === status);
    const color = statusInfo ? statusInfo.color : 'bg-gray-100 text-gray-800';
    const label = statusInfo ? statusInfo.label : status;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded ${color}`}>
        {label}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="h-full flex flex-col px-4 py-1 sm:px-0">
      <ToastContainer toasts={toasts} />
      <div className="bg-white shadow rounded-lg flex flex-col flex-1 overflow-hidden">
        <div className="flex-shrink-0 p-6">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold text-gray-900">Custom Jobs</h1>
            <button
              onClick={openCreateModal}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Add Custom Job
            </button>
          </div>

          {/* Search Bar */}
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Search custom jobs by title, description, or customer..."
              autoComplete="off"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
            <button
              onClick={toggleFilters}
              className={`px-4 py-2 text-sm font-medium border rounded-md ${
                showFilters
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {showFilters ? '\u2715 Clear Filters' : '\u2630 Filters'}
            </button>
          </div>

          {/* Filter Bar */}
          {showFilters && (
            <div className="flex flex-wrap items-center gap-4 mt-3 pt-3 border-t border-gray-200">
              {/* Shop */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600">Shop:</span>
                <select
                  value={filterShopId}
                  onChange={(e) => setFilterShopId(e.target.value)}
                  className="px-2 py-1 border border-gray-300 rounded text-sm"
                >
                  <option value="">Any</option>
                  {shops.map(shop => (
                    <option key={shop.shop_id} value={shop.shop_id}>{shop.shop_id}</option>
                  ))}
                </select>
              </div>
              {/* Date Range */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600">Date:</span>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
                <span className="text-gray-400 text-xs">to</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              {/* Quick Range */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600">Quick Range:</span>
                <select
                  value={quickRange}
                  onChange={handleQuickRangeChange}
                  className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Select Range</option>
                  <option value="this-week">This Week</option>
                  <option value="last-week">Last Week</option>
                  <option value="this-month">This Month</option>
                  <option value="last-month">Last Month</option>
                  <option value="this-quarter">This Quarter</option>
                  <option value="last-quarter">Last Quarter</option>
                  <option value="this-year">This Year</option>
                  <option value="last-year">Last Year</option>
                  <option value="all-time">All Time</option>
                </select>
              </div>
              {/* Status */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600">Status:</span>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-2 py-1 border border-gray-300 rounded text-sm"
                >
                  <option value="any">Any</option>
                  {TICKET_STATUSES.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              {/* Type */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600">Type:</span>
                <select
                  value={filterJewelryType}
                  onChange={(e) => setFilterJewelryType(e.target.value)}
                  className="px-2 py-1 border border-gray-300 rounded text-sm"
                >
                  <option value="any">Any</option>
                  {JEWELRY_TYPES.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              {/* Due */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600">Due:</span>
                <select
                  value={filterDueType}
                  onChange={(e) => setFilterDueType(e.target.value)}
                  className="px-2 py-1 border border-gray-300 rounded text-sm"
                >
                  <option value="any">Any</option>
                  <option value="date">By Date</option>
                  <option value="will-call">Will Call</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Recent Items Banner */}
        {!loading && showingRecent && (
          <div className="flex-shrink-0 bg-blue-50 border-b border-blue-200 px-6 py-2">
            <p className="text-sm text-blue-700 font-medium">Showing {customJobs.length} most recent custom jobs</p>
          </div>
        )}

        {/* Custom Jobs Table Rows */}
        {(() => {
          const customJobsTable = (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shop</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created / Due</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title / Description</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type / Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {customJobs.map(customJob => (
                    <tr key={customJob.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => openEditModal(customJob)}>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/customers/${customJob.customer_id}/dashboard`);
                          }}
                          className="text-indigo-700 hover:text-indigo-900 font-medium"
                          title={`Go to ${customJob.customer_name}'s dashboard`}
                        >
                          {customJob.customer_name}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        {customJob.shop_id ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">{customJob.shop_id}</span>
                        ) : <span className="text-xs text-gray-400">-</span>}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <div>{formatDate(customJob.created_at)}</div>
                        <div className="font-semibold">
                          {customJob.due_type === 'will-call' ? (
                            <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">Will Call</span>
                          ) : formatDate(customJob.due_date)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{customJob.title}</div>
                        {customJob.description && (
                          <div className="text-xs text-gray-400 truncate max-w-xs">
                            {customJob.description.substring(0, 60)}{customJob.description.length > 60 ? '...' : ''}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-600">{customJob.jewelry_type || '-'}</div>
                        {getStatusBadge(customJob.ticket_status)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{formatCurrency(customJob.price)}</td>
                      <td className="px-4 py-3 text-sm">
                        {customJob.transaction_id ? (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">#{customJob.transaction_id}</span>
                        ) : <span className="text-xs text-gray-400">-</span>}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex gap-2">
                          <button onClick={(e) => { e.stopPropagation(); handlePrintTicket(customJob.id); }} className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-14V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                            Print
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
          );

          return (
            <div className="flex-1 overflow-auto px-6 pb-6">
              {loading ? (
                <div className="text-center py-8 text-gray-500">Loading...</div>
              ) : customJobs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {showingRecent
                    ? 'No custom jobs found'
                    : searchQuery.length >= 3 || hasActiveFilters()
                      ? 'No custom jobs found matching your search'
                      : 'No custom jobs found'}
                </div>
              ) : customJobsTable}
            </div>
          );
        })()}
      </div>

      <CustomJobModal
        isOpen={showModal}
        onClose={handleModalClose}
        customJob={editingCustomJob}
        onSuccess={handleModalSuccess}
        onDelete={() => handleDelete(editingCustomJob?.id)}
        onGoToTransaction={handleGoToTransaction}
      />

      <TransactionModal
        isOpen={showTransactionModal}
        onClose={handleTransactionModalClose}
        customer={null}
        editingTransaction={editingTransaction}
        onSuccess={handleTransactionModalSuccess}
      />
    </div>
  );
}
